// Official company-letterhead print for the Owner Settlement Statement.
// Produces a formal A4 document focused purely on Stock Capital, Sales, COGS, and Profit.

const esc = (v) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const money = (v) =>
  num(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const qty = (v) => num(v).toLocaleString('en-US', { maximumFractionDigits: 2 });

export function printOwnerStatement({
  settings = {},
  preparedBy = '',
  selectedMonth,
  displayMonth,
  figures,
  stockInventory = [],
}) {
  const company = {
    name: settings.company_name || 'SUGIMCO',
    subtitle: settings.company_subtitle || 'SUPER MARCHE Yazbek',
    logo: settings.company_logo || '',
    nif: settings.company_nif || '',
    rc: settings.company_rc || '',
    phone: settings.company_phone || '',
    phone2: settings.company_phone2 || '',
    address: settings.company_address || '',
    email: settings.company_email || '',
  };

  const {
    totalBranchesCost,
    totalBranchesSellValue,
    initialDebt,
    totalSales,
    cogs,
    wastes,
    remainingDebt,
    monthlyNetProfit,
    totalOwnerEquity,
  } = figures;

  const now = new Date();
  const issuedOn = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const issuedAt = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const reference = `OS-${String(selectedMonth).replace('-', '')}-${String(now.getTime()).slice(-4)}`;
  const margin = totalSales > 0 ? ((monthlyNetProfit / totalSales) * 100).toFixed(1) : '0.0';

  // Period bounds (first/last day of month)
  const [y, m] = String(selectedMonth).split('-').map(Number);
  const periodStart = new Date(y, m - 1, 1).toLocaleDateString('en-GB');
  const periodEnd = new Date(y, m, 0).toLocaleDateString('en-GB');

  // Stock valuation grouped by branch
  const branchMap = {};
  stockInventory.forEach((it) => {
    const k = it.depo_name || 'Unassigned';
    if (!branchMap[k]) branchMap[k] = { items: 0, qty: 0, cost: 0, sell: 0 };
    branchMap[k].items += 1;
    branchMap[k].qty += num(it.quantity);
    branchMap[k].cost += num(it.total_cost);
    branchMap[k].sell += num(it.total_sell_value);
  });
  const branches = Object.entries(branchMap).sort((a, b) => b[1].cost - a[1].cost);

  const contactLines = [
    company.address && esc(company.address),
    [company.phone && `Tel: ${esc(company.phone)}`, company.phone2 && esc(company.phone2)].filter(Boolean).join(' / '),
    company.email && esc(company.email),
    [company.nif && `NIF: ${esc(company.nif)}`, company.rc && `RC: ${esc(company.rc)}`].filter(Boolean).join(' &nbsp;|&nbsp; '),
  ].filter(Boolean);

  const ledgerRow = (ref, label, note, amount, sign = '') => `
    <tr>
      <td class="c ref">${ref}</td>
      <td>${label}<div class="note">${note}</div></td>
      <td class="r amt">${sign}${money(amount)}</td>
    </tr>`;

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${esc(company.name)} - Owner Settlement Statement ${esc(displayMonth)}</title>
<style>
  @page {
    size: A4 portrait;
    margin: 14mm 12mm 18mm 12mm;
    @bottom-right { content: "Page " counter(page) " / " counter(pages); font: 8pt Arial, sans-serif; color: #555; }
  }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body {
    font-family: 'Segoe UI', Arial, Helvetica, sans-serif;
    font-size: 10pt; color: #111; line-height: 1.35;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .sheet { max-width: 186mm; margin: 0 auto; }
  @media screen { body { background: #e5e7eb; padding: 20px 0; } .sheet { background: #fff; padding: 14mm 12mm; box-shadow: 0 2px 12px rgba(0,0,0,.15); } }

  /* Letterhead */
  .lh { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; padding-bottom: 10px; }
  .lh-left { display: flex; align-items: center; gap: 12px; }
  .lh-left img { max-height: 62px; max-width: 120px; object-fit: contain; }
  .co-name { font-size: 20pt; font-weight: 800; letter-spacing: .5px; text-transform: uppercase; color: #0b2545; margin: 0; line-height: 1.05; }
  .co-sub { font-size: 9pt; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #444; margin-top: 3px; }
  .lh-right { text-align: right; font-size: 8.5pt; color: #333; line-height: 1.5; }
  .rule { height: 3px; background: #0b2545; border-bottom: 1px solid #c9a227; margin-bottom: 14px; }

  /* Document title */
  .doc-head { display: flex; justify-content: space-between; align-items: stretch; gap: 14px; margin-bottom: 14px; }
  .doc-title { flex: 1; }
  .doc-title h1 { margin: 0; font-size: 15pt; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; color: #0b2545; }
  .doc-title p { margin: 4px 0 0; font-size: 9pt; color: #444; }
  .meta { border: 1px solid #0b2545; min-width: 68mm; font-size: 8.5pt; border-collapse: collapse; }
  .meta td { padding: 3px 8px; border-bottom: 1px solid #d4d8de; }
  .meta td:first-child { font-weight: 700; text-transform: uppercase; color: #0b2545; background: #f1f4f8; width: 40%; }
  .meta tr:last-child td { border-bottom: 0; }

  /* Sections */
  h2.sec { font-size: 10pt; text-transform: uppercase; letter-spacing: 1px; color: #fff; background: #0b2545; padding: 5px 8px; margin: 16px 0 0; }
  table.grid { width: 100%; border-collapse: collapse; font-size: 9pt; }
  table.grid th { background: #f1f4f8; color: #0b2545; font-size: 8pt; text-transform: uppercase; letter-spacing: .4px; padding: 6px; border: 1px solid #b8c0cc; text-align: left; }
  table.grid td { padding: 5px 6px; border: 1px solid #d4d8de; vertical-align: top; }
  table.grid tbody tr:nth-child(even) td { background: #fafbfc; }
  .r { text-align: right; } .c { text-align: center; }
  .ref { width: 34px; font-weight: 700; color: #0b2545; }
  .amt { width: 40mm; font-variant-numeric: tabular-nums; font-weight: 600; white-space: nowrap; }
  .note { font-size: 7.5pt; color: #666; margin-top: 1px; }
  .grp td { background: #e8edf3 !important; font-weight: 800; font-size: 8.5pt; text-transform: uppercase; letter-spacing: .5px; color: #0b2545; }
  .sub td { font-weight: 800; background: #f1f4f8 !important; border-top: 1.5px solid #0b2545; }
  .grand td { font-weight: 800; font-size: 10.5pt; background: #0b2545 !important; color: #fff; border-color: #0b2545; }
  .grand .note { color: #cfd8e3; }
  tfoot td { font-weight: 800; background: #f1f4f8; border-top: 1.5px solid #0b2545; }

  /* Key figures */
  .kf { display: grid; grid-template-columns: repeat(4, 1fr); border: 1px solid #b8c0cc; border-top: 0; }
  .kf div { padding: 7px 8px; border-right: 1px solid #d4d8de; border-top: 1px solid #d4d8de; }
  .kf div:nth-child(4n) { border-right: 0; }
  .kf span { display: block; font-size: 7pt; text-transform: uppercase; letter-spacing: .5px; color: #555; font-weight: 700; }
  .kf b { display: block; font-size: 10.5pt; margin-top: 2px; font-variant-numeric: tabular-nums; }

  .declaration { margin-top: 14px; font-size: 8.5pt; color: #333; border-left: 3px solid #c9a227; padding: 6px 10px; background: #fbf8ee; }

  .signs { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-top: 22px; page-break-inside: avoid; }
  .sign { border: 1px solid #b8c0cc; height: 30mm; padding: 6px 8px; display: flex; flex-direction: column; justify-content: space-between; }
  .sign .t { font-size: 8pt; font-weight: 800; text-transform: uppercase; color: #0b2545; }
  .sign .l { border-top: 1px dotted #666; font-size: 7.5pt; color: #666; padding-top: 3px; }

  .footer { margin-top: 18px; text-align: center; font-size: 7.5pt; color: #555; border-top: 1px solid #b8c0cc; padding-top: 4px; }

  tr { page-break-inside: avoid; }
  thead { display: table-header-group; }
  .avoid { page-break-inside: avoid; }
</style>
</head>
<body>
<div class="sheet">

  <!-- Company Letterhead -->
  <header class="lh">
    <div class="lh-left">
      ${company.logo ? `<img src="${esc(company.logo)}" alt="Logo">` : ''}
      <div>
        <p class="co-name">${esc(company.name)}</p>
        <div class="co-sub">${esc(company.subtitle)}</div>
      </div>
    </div>
    <div class="lh-right">${contactLines.join('<br>')}</div>
  </header>
  <div class="rule"></div>

  <!-- Document Heading -->
  <section class="doc-head">
    <div class="doc-title">
      <h1>Owner Settlement Statement</h1>
      <p>Consignment capital, stock valuation &amp; debt settlement for the period
         <b>${periodStart}</b> to <b>${periodEnd}</b>.</p>
    </div>
    <table class="meta">
      <tr><td>Reference</td><td>${reference}</td></tr>
      <tr><td>Period</td><td>${esc(displayMonth)}</td></tr>
      <tr><td>Issue Date</td><td>${issuedOn}</td></tr>
      <tr><td>Currency</td><td>XAF (CFA Franc)</td></tr>
    </table>
  </section>

  <!-- Key figures -->
  <h2 class="sec">1. Key Figures</h2>
  <div class="kf">
    <div><span>Stock Cost (Capital)</span><b>${money(totalBranchesCost)}</b></div>
    <div><span>Stock Retail Value</span><b>${money(totalBranchesSellValue)}</b></div>
    <div><span>Total Sales</span><b>${money(totalSales)}</b></div>
    <div><span>Cost of Sales (COGS)</span><b>${money(cogs)}</b></div>
    <div><span>Wastes &amp; Damaged</span><b>${money(wastes)}</b></div>
    <div><span>Net Profit (${margin}%)</span><b>${money(monthlyNetProfit)}</b></div>
    <div><span>Remaining Debt</span><b>${money(remainingDebt)}</b></div>
    <div><span>Total Owner Returns</span><b>${money(totalOwnerEquity)}</b></div>
  </div>

  <!-- Settlement Ledger -->
  <h2 class="sec">2. Settlement Statement</h2>
  <table class="grid">
    <thead><tr><th class="c">Ref</th><th>Description</th><th class="r">Amount (CFA)</th></tr></thead>
    <tbody>
      <tr class="grp"><td colspan="3">A. Capital &amp; Stock Position</td></tr>
      ${ledgerRow('A1', 'Total branches stock cost (capital invested)', 'Owner total stock capital across all branches (Stock at purchase cost)', totalBranchesCost)}
      ${ledgerRow('A2', 'Total stock retail valuation', `Projected selling worth &mdash; potential retail gain +${money(totalBranchesSellValue - totalBranchesCost)}`, totalBranchesSellValue)}
      ${ledgerRow('A3', 'Original consignment capital (initial debt)', 'Initial debt carried forward prior to this month', initialDebt)}

      <tr class="grp"><td colspan="3">B. Monthly Operations</td></tr>
      ${ledgerRow('B1', 'Monthly revenue (total sales)', 'Total sales revenue collected in this month', totalSales, '+')}
      ${ledgerRow('B2', 'Monthly COGS (cost of sales settled)', 'Cost of goods sold deducted from inventory', cogs, '-')}
      ${ledgerRow('B3', 'Wastes &amp; damaged goods deducted', 'Documented losses from damaged or expired items', wastes, '-')}

      <tr class="grp"><td colspan="3">C. Settlement Result</td></tr>
      <tr class="sub"><td class="c ref">C1</td><td>Remaining supermarket debt (carried forward)<div class="note">Base capital &minus; COGS &minus; wastes</div></td><td class="r amt">${money(remainingDebt)}</td></tr>
      <tr class="sub"><td class="c ref">C2</td><td>Monthly net profit (all branches)<div class="note">Sales &minus; COGS &minus; wastes &nbsp;|&nbsp; margin ${margin}%</div></td><td class="r amt">${money(monthlyNetProfit)}</td></tr>
      <tr class="grand"><td class="c ref" style="color:#fff">C3</td><td>TOTAL OWNER RETURNS<div class="note">Stock capital + monthly profit &mdash; total comprehensive settlement</div></td><td class="r amt">${money(totalOwnerEquity)}</td></tr>
    </tbody>
  </table>

  <!-- Stock by branch -->
  ${branches.length ? `
  <div class="avoid">
  <h2 class="sec">3. Stock Valuation by Branch</h2>
  <table class="grid">
    <thead><tr><th class="c">#</th><th>Branch / Warehouse</th><th class="r">Products</th><th class="r">Quantity</th><th class="r">Stock Cost</th><th class="r">Retail Value</th><th class="r">Potential Gain</th></tr></thead>
    <tbody>
      ${branches.map(([name, b], i) => `
      <tr>
        <td class="c">${i + 1}</td><td>${esc(name)}</td><td class="r">${b.items}</td><td class="r">${qty(b.qty)}</td>
        <td class="r amt">${money(b.cost)}</td><td class="r amt">${money(b.sell)}</td><td class="r amt">${money(b.sell - b.cost)}</td>
      </tr>`).join('')}
    </tbody>
    <tfoot><tr>
      <td colspan="2">Total</td><td class="r">${stockInventory.length}</td><td class="r">${qty(branches.reduce((s, [, b]) => s + b.qty, 0))}</td>
      <td class="r">${money(totalBranchesCost)}</td><td class="r">${money(totalBranchesSellValue)}</td><td class="r">${money(totalBranchesSellValue - totalBranchesCost)}</td>
    </tr></tfoot>
  </table>
  </div>` : ''}

  <div class="declaration avoid">
    We hereby certify that this statement reflects the consignment capital, stock valuation and financial settlement position of
    <b>${esc(company.name)}</b> towards the owner for the period of <b>${esc(displayMonth)}</b>, as recorded in the ERP system.
    The remaining debt of <b>${money(remainingDebt)} CFA</b> is carried forward to the next period.
  </div>

  <div class="signs">
    <div class="sign"><span class="t">Prepared by (Finance)</span><span class="l">${esc(preparedBy) || 'Name &amp; signature'}</span></div>
    <div class="sign"><span class="t">Approved by (Management)</span><span class="l">Name, signature &amp; date</span></div>
    <div class="sign"><span class="t">Owner &mdash; Read &amp; Approved</span><span class="l">Signature &amp; company stamp</span></div>
  </div>

  <div class="footer">
    ${esc(company.name)}${company.address ? ' &bull; ' + esc(company.address) : ''}${company.nif ? ' &bull; NIF ' + esc(company.nif) : ''}${company.rc ? ' &bull; RC ' + esc(company.rc) : ''}
    &bull; Ref ${reference} &bull; Printed ${issuedOn} ${issuedAt}
  </div>
</div>
<script>
  (function () {
    var imgs = Array.prototype.slice.call(document.images);
    var pending = imgs.filter(function (i) { return !i.complete; });
    function go() { setTimeout(function () { window.focus(); window.print(); }, 150); }
    window.onafterprint = function () { window.close(); };
    if (!pending.length) return go();
    var left = pending.length;
    pending.forEach(function (i) { i.onload = i.onerror = function () { if (--left === 0) go(); }; });
  })();
</script>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=1000,height=800,scrollbars=yes');
  if (!win) {
    alert('Please allow popups for this site to print the statement.');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}
